<template>
  <div class="suggestion-settings p-3">
    <h6 class="mb-1">{{ $t('suggestions') }}</h6>
    <p class="text-muted small mb-4">{{ $t('suggestions_settings_description') }}</p>

    <div class="card border mb-4">
      <div class="card-body">
        <div class="row g-3 align-items-end">
          <div class="col-lg-7">
            <label for="newSuggestionContent" class="form-label fw-bold">{{ $t('suggestion_information') }}</label>
            <textarea
              id="newSuggestionContent"
              v-model="newSuggestion.content"
              class="form-control"
              rows="2"
              maxlength="5000"
              :placeholder="$t('suggestion_example')"
            ></textarea>
          </div>
          <div class="col-md-5 col-lg-3">
            <label for="newSuggestionApplication" class="form-label fw-bold">{{ $t('apply_suggestion_to') }}</label>
            <select id="newSuggestionApplication" v-model="newSuggestion.applies_to" class="form-select">
              <option v-for="option in applicationOptions" :key="option.value" :value="option.value">
                {{ $t(option.label) }}
              </option>
            </select>
          </div>
          <div class="col-md-auto col-lg-2">
            <div class="form-check form-switch mb-2">
              <input id="newSuggestionEnabled" v-model="newSuggestion.enabled" class="form-check-input" type="checkbox">
              <label class="form-check-label" for="newSuggestionEnabled">{{ $t('enabled') }}</label>
            </div>
            <button
              type="button"
              class="btn btn-success btn-sm w-100"
              :disabled="creating || !newSuggestion.content.trim()"
              @click="createSuggestion"
            >
              <span v-if="creating" class="spinner-border spinner-border-sm me-1"></span>
              <AppIcon name="bi-plus-circle" v-else class="bi bi-plus-circle me-1" />{{ $t('add') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="loading" class="text-center py-4">
      <span class="spinner-border spinner-border-sm text-secondary"></span>
    </div>
    <div v-else-if="!suggestions.length" class="text-center text-muted border rounded py-4">
      <AppIcon name="bi-chat-square-text" class="bi bi-chat-square-text d-block fs-3 mb-2" />
      {{ $t('no_suggestions_added') }}
    </div>
    <div v-else class="list-group">
      <div v-for="suggestion in suggestions" :key="suggestion.id" class="list-group-item p-3">
        <div class="row g-3 align-items-center">
          <div class="col-lg-7">
            <textarea v-model="suggestion.content" class="form-control form-control-sm" rows="2" maxlength="5000"></textarea>
          </div>
          <div class="col-md-5 col-lg-3">
            <select v-model="suggestion.applies_to" class="form-select form-select-sm">
              <option v-for="option in applicationOptions" :key="option.value" :value="option.value">
                {{ $t(option.label) }}
              </option>
            </select>
          </div>
          <div class="col-md-7 col-lg-2 d-flex justify-content-end align-items-center gap-2">
            <div class="form-check form-switch m-0" :title="suggestion.enabled ? $t('enabled') : $t('disabled')">
              <input
                v-model="suggestion.enabled"
                class="form-check-input"
                type="checkbox"
                :disabled="savingId === suggestion.id"
                @change="updateSuggestion(suggestion)"
              >
            </div>
            <button
              type="button"
              class="btn btn-outline-success btn-sm"
              :disabled="savingId === suggestion.id || !suggestion.content.trim()"
              :title="$t('save')"
              @click="updateSuggestion(suggestion)"
            >
              <span v-if="savingId === suggestion.id" class="spinner-border spinner-border-sm"></span>
              <AppIcon name="bi-check-lg" v-else class="bi bi-check-lg" />
            </button>
            <button
              type="button"
              class="btn btn-outline-danger btn-sm"
              :disabled="savingId === suggestion.id"
              :title="$t('delete')"
              @click="deleteSuggestion(suggestion)"
            >
              <AppIcon name="bi-trash" class="bi bi-trash" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import helper from '../../helper'

export default {
  name: 'SuggestionSettings',
  data() {
    return {
      suggestions: [],
      loading: false,
      creating: false,
      savingId: null,
      applicationOptions: [
        { value: 'invoice_notes', label: 'invoice_notes' }
      ],
      newSuggestion: {
        content: '',
        applies_to: 'invoice_notes',
        enabled: true
      }
    }
  },
  methods: {
    async loadSuggestions() {
      try {
        this.loading = true
        const response = await helper.customGet(`${process.env.VUE_APP_API}/suggestions`)
        this.suggestions = response.data.data || []
      } catch (error) {
        this.$toast.error(this.$t('failed_to_load_suggestions'))
      } finally {
        this.loading = false
      }
    },
    async createSuggestion() {
      const content = this.newSuggestion.content.trim()
      if (!content) return

      try {
        this.creating = true
        const response = await helper.customPost(`${process.env.VUE_APP_API}/suggestions`, {
          ...this.newSuggestion,
          content,
          sort_order: this.suggestions.length
        })
        this.suggestions.push(response.data)
        this.newSuggestion.content = ''
        this.$toast.success(this.$t('suggestion_created'))
      } catch (error) {
        this.$toast.error(this.$t('failed_to_save_suggestion'))
      } finally {
        this.creating = false
      }
    },
    async updateSuggestion(suggestion) {
      const content = suggestion.content.trim()
      if (!content) return

      try {
        this.savingId = suggestion.id
        const response = await helper.customPatch(`${process.env.VUE_APP_API}/suggestions/${suggestion.id}`, {
          content,
          applies_to: suggestion.applies_to,
          enabled: suggestion.enabled,
          sort_order: suggestion.sort_order || 0
        })
        Object.assign(suggestion, response.data)
        this.$toast.success(this.$t('successfully_saved'))
      } catch (error) {
        this.$toast.error(this.$t('failed_to_save_suggestion'))
        await this.loadSuggestions()
      } finally {
        this.savingId = null
      }
    },
    async deleteSuggestion(suggestion) {
      const result = await this.$swal({
        title: this.$t('are_you_sure'),
        text: this.$t('delete_suggestion_confirmation'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: this.$t('delete'),
        cancelButtonText: this.$t('cancel')
      })
      if (!result.isConfirmed) return

      try {
        this.savingId = suggestion.id
        await helper.customDelete(`${process.env.VUE_APP_API}/suggestions/${suggestion.id}`)
        this.suggestions = this.suggestions.filter(item => item.id !== suggestion.id)
        this.$toast.success(this.$t('successfully_deleted'))
      } catch (error) {
        this.$toast.error(this.$t('failed_to_delete_suggestion'))
      } finally {
        this.savingId = null
      }
    }
  },
  mounted() {
    this.loadSuggestions()
  }
}
</script>
